import { useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import { useAuthStore } from '../store/useAuthStore';
import Header from '../components/layout/Header';
import { getContact, deleteContact } from '../services/apiContacts';
import { createNote, deleteNote } from '../services/apiNotes';
import { createActivity, completeActivity } from '../services/apiActivities';
import { FOLLOW_UP_TYPES, getFollowUpTypeMeta, getDefaultFollowUpDate } from '../lib/followUpTypes';
import { useModules } from '../lib/useModules';
import DictateButton from '../components/common/DictateButton';
import { createDeal } from '../services/apiDeals';
import { resolveMediaUrl } from '../lib/utils';
import { CURRENCIES, currencyOptionLabel, tenantDefaultCurrency } from '../lib/currencies';
import ContactModal from '../components/contacts/ContactModal';
import ContactQuotesCard from '../components/contacts/ContactQuotesCard';
import EmailViewerModal from '../components/contacts/EmailViewerModal';
import MergeContactModal from '../components/contacts/MergeContactModal';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// ─── File type helper ────────────────────────────────────────────────────────
function getFileInfo(mimeType, mediaType, filename) {
    const mime = (mimeType || '').toLowerCase();
    const name = (filename || '').toLowerCase();

    if (mediaType === 'image' || mediaType === 'sticker' || mime.startsWith('image/')) {
        return { icon: 'image', color: 'text-violet-500', bg: 'bg-violet-50 dark:bg-violet-900/20', label: 'Imagen' };
    }
    if (mediaType === 'audio' || mediaType === 'voice_note' || mime.startsWith('audio/')) {
        return { icon: 'mic', color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20', label: 'Audio' };
    }
    if (mediaType === 'video' || mime.startsWith('video/')) {
        return { icon: 'videocam', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', label: 'Video' };
    }
    if (mime.includes('pdf') || name.endsWith('.pdf')) {
        return { icon: 'picture_as_pdf', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20', label: 'PDF' };
    }
    if (mime.includes('spreadsheet') || mime.includes('excel') || name.match(/\.(xlsx?|csv)$/)) {
        return { icon: 'table_chart', color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20', label: 'Hoja de cálculo' };
    }
    if (mime.includes('word') || mime.includes('document') || name.match(/\.(docx?)$/)) {
        return { icon: 'description', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', label: 'Documento' };
    }
    if (mime.includes('presentation') || mime.includes('powerpoint') || name.match(/\.(pptx?)$/)) {
        return { icon: 'slideshow', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', label: 'Presentación' };
    }
    if (mime.includes('zip') || mime.includes('rar') || mime.includes('tar') || name.match(/\.(zip|rar|7z|tar|gz)$/)) {
        return { icon: 'folder_zip', color: 'text-yellow-600', bg: 'bg-yellow-50 dark:bg-yellow-900/20', label: 'Archivo comprimido' };
    }
    return { icon: 'attach_file', color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-800', label: 'Archivo' };
}

function ChannelBadge({ channel }) {
    if (channel === 'instagram') {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r from-pink-500 to-purple-500 text-white">
                <span className="material-symbols-outlined text-[10px]">photo_camera</span>
                Instagram
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <span className="material-symbols-outlined text-[10px]">chat</span>
            WhatsApp
        </span>
    );
}

// ─── Files Panel ─────────────────────────────────────────────────────────────
function FilesPanel({ files }) {
    const [filter, setFilter] = useState('all');

    const filters = [
        { key: 'all',    label: 'Todos' },
        { key: 'image',  label: 'Imágenes' },
        { key: 'document', label: 'Docs' },
        { key: 'audio',  label: 'Audio' },
        { key: 'video',  label: 'Video' },
    ];

    const filtered = filter === 'all' ? files : files.filter(f => {
        if (filter === 'image')    return f.fileInfo.label === 'Imagen';
        if (filter === 'document') return ['PDF', 'Documento', 'Hoja de cálculo', 'Presentación', 'Archivo', 'Archivo comprimido'].includes(f.fileInfo.label);
        if (filter === 'audio')    return f.fileInfo.label === 'Audio';
        if (filter === 'video')    return f.fileInfo.label === 'Video';
        return true;
    });

    return (
        <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
                <div className="flex items-center gap-2 mb-3">
                    <span className="material-symbols-outlined text-primary text-lg">folder_open</span>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Archivos Compartidos</h3>
                    {files.length > 0 && (
                        <span className="ml-auto text-xs font-medium text-white bg-primary rounded-full px-2 py-0.5">{files.length}</span>
                    )}
                </div>
                {/* Filter tabs */}
                <div className="flex flex-wrap gap-1">
                    {filters.map(f => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                                filter === f.key
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* File list */}
            <div className="divide-y divide-gray-100 dark:divide-gray-800 max-h-[480px] overflow-y-auto no-scrollbar">
                {filtered.length === 0 ? (
                    <div className="py-10 flex flex-col items-center gap-2 text-center px-4">
                        <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-700">folder_open</span>
                        <p className="text-xs text-gray-400">No hay archivos en esta categoría.</p>
                    </div>
                ) : (
                    (filtered || []).map((file, i) => {
                        const fi = file.fileInfo;
                        return (
                            <div
                                key={i}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group cursor-pointer"
                                onClick={() => file.url && window.open(file.url, '_blank')}
                            >
                                {/* Icon */}
                                <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${fi.bg} flex items-center justify-center`}>
                                    <span className={`material-symbols-outlined text-lg ${fi.color}`}>{fi.icon}</span>
                                </div>

                                {/* Info */}
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold text-gray-900 dark:text-white truncate leading-tight">
                                        {file.name || fi.label}
                                    </p>
                                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                        {file.channel === 'instagram' ? (
                                            <span className="text-[10px] text-pink-500 font-medium">Instagram</span>
                                        ) : file.channel === 'email' ? (
                                            <span className="text-[10px] text-blue-500 font-medium">Email</span>
                                        ) : (
                                            <span className="text-[10px] text-green-600 font-medium">WhatsApp</span>
                                        )}
                                        <span className="text-[10px] text-gray-400">·</span>
                                        <span className="text-[10px] text-gray-400">{dayjs(file.date).fromNow()}</span>
                                        <span className="text-[10px] uppercase tracking-wide font-bold text-gray-400 ml-0.5">{fi.label}</span>
                                    </div>
                                </div>

                                {/* Action */}
                                {file.url ? (
                                    <span className="material-symbols-outlined text-gray-300 dark:text-gray-700 group-hover:text-primary transition-colors text-lg flex-shrink-0">open_in_new</span>
                                ) : (
                                    <span className="material-symbols-outlined text-gray-200 dark:text-gray-800 text-lg flex-shrink-0">attachment</span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {files.length === 0 && (
                <div className="px-4 py-6 text-center">
                    <span className="material-symbols-outlined text-4xl text-gray-200 dark:text-gray-800 block mb-2">cloud_off</span>
                    <p className="text-xs text-gray-400">Aún no hay archivos compartidos con este contacto.</p>
                </div>
            )}
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Contact360View() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const role = useAuthStore(state => state.user?.role);
    const authTenant = useAuthStore(state => state.tenant);
    const dealDefaultCurrency = tenantDefaultCurrency(authTenant);
    const isAdminOrManager = ['admin', 'manager', 'superadmin'].includes(role);

    const { isFeatureEnabled, isModuleEnabled } = useModules();
    const canUseNotes = isFeatureEnabled('contacts', 'notes');
    const canUseActivities = isFeatureEnabled('contacts', 'activities');
    const canSeeAiTraces = isAdminOrManager && isFeatureEnabled('contacts', 'ai_traces');
    const canEditContact = isFeatureEnabled('contacts', 'edit');
    const canDeleteContact = isFeatureEnabled('contacts', 'delete');
    const canMergeContacts = isFeatureEnabled('contacts', 'merge');
    const canUseEmail = isFeatureEnabled('settings', 'email');

    // La pestaña de Email en el 360 sólo se activa si hay al menos una
    // cuenta de correo conectada — de lo contrario nunca habría nada que
    // mostrar ahí y sólo confundiría al usuario.
    const { data: emailAccountsResponse } = useQuery({
        queryKey: ['email-accounts'],
        queryFn: () => axios.get('/api/email-accounts'),
        enabled: canUseEmail,
    });
    const hasEmailAccounts = (emailAccountsResponse?.data || []).length > 0;
    const canUseDeals = isModuleEnabled('deals');

    // La primera pestaña disponible del compositor: si Notas está apagada no
    // tiene sentido abrir ahí (quedaría un cuadro que no puede guardar nada).
    const defaultComposerTab = canUseNotes ? 'note' : canUseActivities ? 'followup' : canSeeAiTraces ? 'ai-log' : null;

    const [activeComposerTab, setActiveComposerTab] = useState(defaultComposerTab);
    const [noteBody, setNoteBody] = useState('');
    const [followUpForm, setFollowUpForm] = useState({ type: 'call', title: '', scheduled_at: getDefaultFollowUpDate(), description: '' });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isAddDealModalOpen, setIsAddDealModalOpen] = useState(false);
    const [dealFormData, setDealFormData] = useState({
        title: '',
        value: '',
        currency: dealDefaultCurrency,
        stage: 'prospecting',
        expected_close_date: ''
    });

    const createDealMutation = useMutation({
        mutationFn: (dealData) => createDeal({ ...dealData, contact_id: id }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
            setIsAddDealModalOpen(false);
            setDealFormData({
                title: '',
                value: '',
                currency: 'USD',
                stage: 'prospecting',
                expected_close_date: ''
            });
        },
        onError: (err) => {
            alert(err.response?.data?.message || 'Error al crear negocio');
        }
    });
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [selectedEmailId, setSelectedEmailId] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [timelineFilter, setTimelineFilter] = useState('todo');
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
    const [isDeleteContactModalOpen, setIsDeleteContactModalOpen] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    const { data: contact, isLoading, error } = useQuery({
        queryKey: ['contact', id],
        queryFn: () => getContact(id),
    });

    const createNoteMutation = useMutation({
        mutationFn: createNote,
        onSuccess: () => {
            setNoteBody('');
            setAttachments([]);
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
        }
    });

    const deleteNoteMutation = useMutation({
        mutationFn: deleteNote,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
        }
    });

    const createActivityMutation = useMutation({
        mutationFn: createActivity,
        onSuccess: () => {
            setFollowUpForm({ type: 'call', title: '', scheduled_at: getDefaultFollowUpDate(), description: '' });
            setActiveComposerTab('note');
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
        },
        onError: (err) => {
            alert(err.response?.data?.message || 'Error al agendar el seguimiento');
        }
    });

    const completeActivityMutation = useMutation({
        mutationFn: completeActivity,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
        }
    });

    const handleScheduleFollowUp = () => {
        if (!followUpForm.title.trim()) return;
        createActivityMutation.mutate({
            contact_id: contact.id,
            type: followUpForm.type,
            title: followUpForm.title.trim(),
            description: followUpForm.description || null,
            scheduled_at: followUpForm.scheduled_at || null,
        });
    };

    const deleteContactMutation = useMutation({
        mutationFn: () => deleteContact(id),
        onSuccess: () => {
            navigate('/contacts');
        },
        onError: (err) => {
            const msg = err.response?.data?.message || 'Error al eliminar el lead';
            alert(msg);
        }
    });

    const toggleAutopilotMutation = useMutation({
        mutationFn: async () => {
            const { default: axiosInstance } = await import('../lib/axios');
            return axiosInstance.post(`/api/leads/${id}/toggle-autopilot`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
        }
    });

    const toggleIgnoreMutation = useMutation({
        mutationFn: async () => {
            const { default: axiosInstance } = await import('../lib/axios');
            return axiosInstance.post(`/api/leads/${id}/toggle-ignore`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
        }
    });

    const evaluateLeadMutation = useMutation({
        mutationFn: async () => {
            const { default: axiosInstance } = await import('../lib/axios');
            return axiosInstance.post(`/api/leads/${id}/evaluate`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contact', id] });
        },
        onError: (err) => {
            const msg = err.response?.data?.message || 'Error al calificar el lead';
            alert(msg);
        }
    });

    const { data: aiTracesResponse, isLoading: isLoadingTraces } = useQuery({
        queryKey: ['aiTraces', id],
        queryFn: async () => {
            const { default: axiosInstance } = await import('../lib/axios');
            const response = await axiosInstance.get(`/api/contacts/${id}/ai-traces`);
            return response.data;
        },
        enabled: !!id && isAdminOrManager,
    });

    const aiTraces = aiTracesResponse?.data || [];

    const handleSaveNote = () => {
        if (!noteBody.trim() && attachments.length === 0) return;
        const formData = new FormData();
        formData.append('notable_type', 'contact');
        formData.append('notable_id', contact.id);
        formData.append('body', noteBody);
        attachments.forEach(file => formData.append('attachments[]', file));
        createNoteMutation.mutate(formData);
    };

    const handleDeleteClick = (noteId, e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (!noteId) return;
        setNoteToDelete(noteId);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        if (!noteToDelete) return;
        deleteNoteMutation.mutate(noteToDelete, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['contact', id] });
                setIsDeleteModalOpen(false);
                setNoteToDelete(null);
            },
            onError: (err) => {
                setIsDeleteModalOpen(false);
                setNoteToDelete(null);
                alert('No se pudo eliminar: ' + (err.response?.data?.message || err.message));
            }
        });
    };

    const handleDownload = async (url, filename, e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const { default: axiosInstance } = await import('../lib/axios');
            const response = await axiosInstance.get('/api/notes/download-attachment', {
                params: { path: url, name: filename },
                responseType: 'blob'
            });
            const blob = response.data;
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error('Error downloading file', err);
            const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            const fallbackUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
            window.open(fallbackUrl, '_blank');
        }
    };

    const insertFormat = (formatStart, formatEnd = '') => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = noteBody.substring(start, end);
        const newText = noteBody.substring(0, start) + formatStart + selectedText + formatEnd + noteBody.substring(end);
        setNoteBody(newText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + formatStart.length, end + formatStart.length);
        }, 0);
    };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files?.length > 0) {
            setAttachments(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
            e.dataTransfer.clearData();
        }
    };
    const handleFileChange = (e) => {
        if (e.target.files?.length > 0) setAttachments(prev => [...prev, ...Array.from(e.target.files)]);
    };
    const removeAttachment = (index) => setAttachments(prev => prev.filter((_, i) => i !== index));

    // ── Loading / Error ──
    if (isLoading) {
        return (
            <div className="flex-1 flex flex-col items-center justify-center p-8">
                <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
            </div>
        );
    }
    if (error || !contact) {
        return (
            <div className="flex-1 flex flex-col p-8">
                <Header title="Vista 360" />
                <div className="text-center text-red-500 mt-10">Error al cargar el contacto</div>
            </div>
        );
    }

    const initials = contact.full_name ? contact.full_name.substring(0, 2).toUpperCase() : 'CN';

    // ── Build timeline items ──
    const realNotes = (contact.notes || []).map(n => ({
        id: `note-${n.id}`,
        noteId: n.id,
        type: 'note',
        title: 'Nota Interna',
        user: n.user?.name || 'Usuario',
        body: n.body,
        attachments: n.attachments || [],
        created_at: n.created_at,
    }));

    const realActivities = (contact.activities || []).map(a => ({
        id: `act-${a.id}`,
        activityId: a.id,
        type: a.type,
        title: a.title,
        user: a.user?.name || 'Sistema',
        body: a.description,
        metadata: a.metadata || {},
        created_at: a.created_at,
        scheduled_at: a.scheduled_at,
        is_completed: a.is_completed,
        is_overdue: a.is_overdue,
    }));

    const realConversations = (contact.conversations || []).map(c => ({
        id: `conv-${c.id}`,
        conversationId: c.id,
        type: 'conversation',
        channel: c.channel,
        title: c.channel === 'instagram' ? 'Conversación de Instagram' : 'Conversación de WhatsApp',
        user: contact.full_name || 'Contacto',
        body: c.latest_message ? c.latest_message.body : 'Sin mensajes aún',
        unread_count: c.unread_count || 0,
        created_at: c.last_message_at || c.created_at,
    }));

    // Email messages from EmailMessage model (synced via Gmail/Outlook)
    const realEmailMessages = (contact.email_messages || []).map(em => ({
        id: `email-${em.id}`,
        emailId: em.id,
        type: 'email',
        title: em.subject || '(Sin asunto)',
        user: em.is_inbound ? em.from_email : em.to_email,
        body: em.snippet || '',
        metadata: {
            email_message_id: em.id,
            is_inbound: em.is_inbound,
            has_attachments: em.has_attachments,
            from_email: em.from_email,
            to_email: em.to_email,
        },
        created_at: em.date,
    }));

    const realMedia = (contact.media_messages || []).map(m => ({
        id: `media-${m.id}`,
        messageId: m.id,
        conversationId: m.conversation_id,
        type: 'media',
        channel: m.channel || 'whatsapp',
        title: m.direction === 'inbound' ? 'Archivo Recibido' : 'Archivo Enviado',
        user: contact.full_name || 'Contacto',
        mediaType: m.type,
        mediaUrl: m.media_url,
        mediaMime: m.media_mime_type,
        body: m.body || '',
        metadata: m.metadata || {},
        created_at: m.created_at,
    }));

    // ── Sort timeline ──
    let combinedTimeline = [...realNotes, ...realActivities, ...realConversations, ...realEmailMessages, ...realMedia]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    if (timelineFilter === 'notas')    combinedTimeline = combinedTimeline.filter(i => i.type === 'note');
    if (timelineFilter === 'chats')    combinedTimeline = combinedTimeline.filter(i => i.type === 'conversation');
    if (timelineFilter === 'emails')   combinedTimeline = combinedTimeline.filter(i => i.type === 'email');
    if (timelineFilter === 'llamadas') combinedTimeline = combinedTimeline.filter(i => i.type === 'call');
    if (timelineFilter === 'archivos') combinedTimeline = combinedTimeline.filter(i => i.type === 'media');

    // ── Build files list for panel ──
    // From WhatsApp/Instagram media messages (Exclude stickers as requested)
    const waFiles = realMedia
        .filter(m => m.mediaType !== 'sticker' && !['location', 'contact', 'business'].includes(m.mediaType))
        .map(m => {
            const fi = getFileInfo(m.mediaMime, m.mediaType, m.body);
            const ext = m.mediaUrl ? m.mediaUrl.split('.').pop().split('?')[0].toUpperCase() : '';
            return {
                name: m.body
                    ? (m.body.length > 40 ? m.body.substring(0, 40) + '…' : m.body)
                    : (fi.label + (ext ? ' .' + ext : '')),
                url: m.mediaUrl,
                fileInfo: fi,
                channel: m.channel || 'whatsapp',
                date: m.created_at,
            };
        });

    // From emails (no direct download URL, just flag for attachments)
    const emailFiles = (contact.email_messages || [])
        .filter(em => em.has_attachments)
        .map(em => ({
            name: `Adjunto — ${em.subject || 'Sin asunto'}`,
            url: null,
            fileInfo: { icon: 'mail', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', label: 'Archivo' },
            channel: 'email',
            date: em.date,
        }));

    const allFiles = [...waFiles, ...emailFiles]
        .sort((a, b) => new Date(b.date) - new Date(a.date));

    return (
        <div className="flex flex-col flex-1 h-full overflow-hidden">
            <Header title="Vista 360" />

            <div className="flex-1 overflow-y-auto no-scrollbar w-full max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                    <Link to="/contacts" className="hover:text-primary transition-colors">Contacts</Link>
                    <span className="material-symbols-outlined text-base">chevron_right</span>
                    <span className="font-medium text-gray-900 dark:text-white">{contact.full_name || 'Sin Nombre'}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                    {/* ── LEFT COLUMN: Profile & Details (3 cols) ── */}
                    <section className="lg:col-span-3 flex flex-col gap-6">
                        {/* Profile Card */}
                        <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 flex flex-col items-center text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-primary/10 to-primary/5"></div>
                            <div className="relative mt-4 mb-4">
                                {contact.avatar ? (
                                    <div className="size-28 rounded-full border-4 border-white dark:border-[#1a100c] shadow-md bg-cover bg-center" style={{ backgroundImage: `url('${resolveMediaUrl(contact.avatar)}')` }}></div>
                                ) : (
                                    <div className="size-28 rounded-full border-4 border-white dark:border-[#1a100c] shadow-md bg-primary/10 text-primary flex items-center justify-center text-4xl font-bold">
                                        {initials}
                                    </div>
                                )}
                                <div className="absolute bottom-1 right-1 bg-green-500 w-5 h-5 rounded-full border-2 border-white dark:border-[#1a100c]" title="Online"></div>
                            </div>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{contact.full_name || 'Sin nombre'}</h1>
                            {contact.position && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{contact.position}</p>}
                            {contact.company && <p className="text-sm text-primary font-medium">{contact.company}</p>}

                            {/* Quick Actions */}
                            <div className="grid grid-cols-3 gap-2 w-full mt-6">
                                <button 
                                    onClick={() => setTimelineFilter('notas')}
                                    className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors group ${timelineFilter === 'notas' ? 'bg-primary/5' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${timelineFilter === 'notas' ? 'bg-primary text-white' : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white'}`}>
                                        <span className="material-symbols-outlined text-xl">sticky_note_2</span>
                                    </div>
                                    <span className={`text-xs font-medium ${timelineFilter === 'notas' ? 'text-primary' : 'text-gray-600 dark:text-gray-300'}`}>Notas</span>
                                </button>
                                <button className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group" onClick={() => window.location.href = `mailto:${contact.email}`}>
                                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                                        <span className="material-symbols-outlined text-xl">mail</span>
                                    </div>
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Email</span>
                                </button>
                                <button className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all">
                                        <span className="material-symbols-outlined text-xl">chat</span>
                                    </div>
                                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">WhatsApp</span>
                                </button>
                            </div>

                            {/* Merge Action */}
                            {canMergeContacts && (
                                <button
                                    onClick={() => setIsMergeModalOpen(true)}
                                    className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-gray-400 hover:border-primary/50 hover:text-primary hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-xs font-medium group"
                                    title="Unificar con otro contacto (mismo cliente en distintos canales)"
                                >
                                    <span className="material-symbols-outlined text-sm group-hover:text-primary transition-colors">merge</span>
                                    Unificar contacto
                                </button>
                            )}

                            {/* Delete Contact Action */}
                            {canDeleteContact && (
                                <button
                                    onClick={() => setIsDeleteContactModalOpen(true)}
                                    className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-red-200 dark:border-red-800/40 text-red-500 hover:border-red-500/50 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all text-xs font-medium group"
                                    title="Eliminar este lead permanentemente"
                                >
                                    <span className="material-symbols-outlined text-sm group-hover:text-red-600 transition-colors">delete</span>
                                    Eliminar lead
                                </button>
                            )}
                        </div>

                        {/* AI Autopilot Card */}
                        <div className={`overflow-hidden rounded-xl shadow-sm border transition-all ${
                            contact.is_ignored
                                ? 'bg-slate-500/5 border-slate-400/30'
                                : contact.is_autopilot_enabled
                                    ? 'bg-yellow-500/5 border-yellow-500/30'
                                    : 'bg-white dark:bg-[#1a100c] border-gray-100 dark:border-gray-800'
                        }`}>
                            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/30 dark:bg-gray-900/30">
                                <div className="flex items-center gap-2">
                                    <span className={`material-symbols-outlined text-lg ${
                                        contact.is_ignored ? 'text-slate-400' : contact.is_autopilot_enabled ? 'text-yellow-500' : 'text-gray-400'
                                    }`}>{contact.is_ignored ? 'voice_over_off' : 'bolt'}</span>
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Piloto Automático</h3>
                                    {contact.is_ignored && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200 dark:border-slate-700">
                                            No Lead
                                        </span>
                                    )}
                                </div>
                                <label className={`relative inline-flex items-center cursor-pointer ${contact.is_ignored ? 'opacity-40 pointer-events-none' : ''}`}>
                                    <input 
                                        type="checkbox" 
                                        className="sr-only peer" 
                                        checked={contact.is_autopilot_enabled && !contact.is_ignored} 
                                        onChange={() => toggleAutopilotMutation.mutate()}
                                        disabled={toggleAutopilotMutation.isPending || contact.is_ignored}
                                    />
                                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500"></div>
                                </label>
                            </div>
                            <div className="p-5">
                                {/* is_ignored banner */}
                                {contact.is_ignored && (
                                    <div className="mb-4 flex items-start gap-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                                        <span className="material-symbols-outlined text-slate-400 text-base mt-0.5">info</span>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                            Este contacto está marcado como <strong>No Lead</strong>. La IA no lo procesará ni consumirá tokens. El chat sigue disponible para el equipo.
                                        </p>
                                    </div>
                                )}
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex flex-col">
                                        <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tight">AI Lead Score</span>
                                        <span className={`text-2xl font-black ${
                                            contact.is_ignored ? 'text-slate-300 dark:text-slate-600' :
                                            contact.lead_score >= 80 ? 'text-green-500' : contact.lead_score >= 50 ? 'text-yellow-500' : 'text-gray-400'
                                        }`}>
                                            {contact.is_ignored ? '—' : `${contact.lead_score ?? 0}%`}
                                        </span>
                                    </div>
                                    <button 
                                        onClick={() => evaluateLeadMutation.mutate()}
                                        disabled={evaluateLeadMutation.isPending || contact.is_ignored}
                                        className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-all group disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={contact.is_ignored ? 'No disponible (contacto ignorado)' : 'Calificar con IA ahora'}
                                    >
                                        <span className={`material-symbols-outlined text-2xl transition-all ${evaluateLeadMutation.isPending ? 'animate-spin text-primary' : 'text-primary opacity-50 group-hover:opacity-100'}`}>
                                            {evaluateLeadMutation.isPending ? 'sync' : 'psychology'}
                                        </span>
                                    </button>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-100 dark:border-gray-800">
                                    <p className="text-xs text-gray-600 dark:text-gray-300 italic">
                                        {contact.is_ignored
                                            ? 'Este contacto es un proveedor, empleado u otro no-lead.'
                                            : (contact.ai_summary || 'Sin resumen de IA aún. Activa el piloto automático para que la IA califique este lead.')}
                                    </p>
                                </div>

                                {/* Ignore Toggle Button */}
                                <button
                                    onClick={() => toggleIgnoreMutation.mutate()}
                                    disabled={toggleIgnoreMutation.isPending}
                                    className={`mt-4 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-bold transition-all ${
                                        contact.is_ignored
                                            ? 'border-green-200 dark:border-green-700/50 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40'
                                            : 'border-dashed border-slate-300 dark:border-slate-700 text-slate-400 hover:border-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                                    }`}
                                    title={contact.is_ignored ? 'Desmarcar como No Lead' : 'Marcar como No Lead (proveedor, empleado, etc.)'}
                                >
                                    {toggleIgnoreMutation.isPending ? (
                                        <span className="material-symbols-outlined text-sm animate-spin">sync</span>
                                    ) : (
                                        <span className="material-symbols-outlined text-sm">{contact.is_ignored ? 'person_add' : 'person_off'}</span>
                                    )}
                                    {contact.is_ignored ? 'Desmarcar como No Lead' : 'Marcar como No Lead'}
                                </button>
                            </div>
                        </div>

                        {/* Basic Info */}
                        <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
                                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Información Básica</h3>
                                {canEditContact && (
                                    <button onClick={() => setIsEditModalOpen(true)} className="text-gray-400 hover:text-primary"><span className="material-symbols-outlined text-lg">edit</span></button>
                                )}
                            </div>
                            <div className="p-5 flex flex-col gap-4">
                                <div className="group">
                                    <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1 block">Email</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-900 dark:text-gray-200 truncate">{contact.email || '—'}</span>
                                        {contact.email && <button className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-primary transition-opacity" onClick={() => navigator.clipboard.writeText(contact.email)} title="Copiar"><span className="material-symbols-outlined text-sm">content_copy</span></button>}
                                    </div>
                                </div>
                                <div className="group">
                                    <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1 block">Phone</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-900 dark:text-gray-200">{contact.phone || '—'}</span>
                                        {contact.phone && <button className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-primary transition-opacity" onClick={() => navigator.clipboard.writeText(contact.phone)} title="Copiar"><span className="material-symbols-outlined text-sm">content_copy</span></button>}
                                    </div>
                                </div>
                                <div className="group">
                                    <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1 block">Empresa</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-900 dark:text-gray-200">{contact.company || '—'}</span>
                                    </div>
                                </div>
                                <div className="group">
                                    <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1 block">Segmentación</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-primary font-bold">{contact.segmentation || '—'}</span>
                                    </div>
                                </div>
                                <div className="group">
                                    <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1 block">Dirección / Ubicación</label>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-900 dark:text-gray-200">{contact.address || '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Custom Fields */}
                        {contact.custom_field_values?.length > 0 && (
                            <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Campos Personalizados</h3>
                                    <button className="text-gray-400 hover:text-primary"><span className="material-symbols-outlined text-lg">edit</span></button>
                                </div>
                                <div className="p-5 grid grid-cols-2 gap-4">
                                    {(contact.custom_field_values || []).map(cfv => (
                                        <div key={cfv.field_id} className="col-span-2">
                                            <label className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1 block">{cfv.field?.name || 'Campo'}</label>
                                            <span className="text-sm text-gray-900 dark:text-gray-200">{cfv.value || '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tags */}
                        {contact.tags?.length > 0 && (
                            <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-5">
                                <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-3">Etiquetas</h3>
                                <div className="flex flex-wrap gap-2">
                                    {(contact.tags || []).map((tag, idx) => (
                                        <span key={idx} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                                            {tag}
                                        </span>
                                    ))}
                                    <button className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-500 transition-colors">
                                        <span className="material-symbols-outlined text-sm">add</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ── CENTER COLUMN: Activity Feed (6 cols) ── */}
                    <section className="lg:col-span-6 flex flex-col gap-6">
                        {/* Note Composer — se oculta entero si no queda ninguna pestaña habilitada */}
                        {defaultComposerTab && (
                        <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                            <div className="flex border-b border-gray-100 dark:border-gray-800">
                                {canUseNotes && (
                                <button
                                    onClick={() => setActiveComposerTab('note')}
                                    className={`flex-1 py-3 text-sm font-medium transition-all ${activeComposerTab === 'note' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                >
                                    <span className="flex items-center justify-center gap-2"><span className="material-symbols-outlined text-lg">edit_note</span> Nota</span>
                                </button>
                                )}
                                {canUseActivities && (
                                <button
                                    onClick={() => setActiveComposerTab('followup')}
                                    className={`flex-1 py-3 text-sm font-medium transition-all ${activeComposerTab === 'followup' ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                >
                                    <span className="flex items-center justify-center gap-2"><span className="material-symbols-outlined text-lg">event_available</span> Seguimiento</span>
                                </button>
                                )}
                                <button className="flex-1 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" title="Funcionalidad próximamente">
                                    <span className="flex items-center justify-center gap-2 opacity-50"><span className="material-symbols-outlined text-lg">mail</span> Email</span>
                                </button>
                                <button className="flex-1 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" title="Funcionalidad próximamente">
                                    <span className="flex items-center justify-center gap-2 opacity-50"><span className="material-symbols-outlined text-lg">sms</span> SMS</span>
                                </button>
                                {canSeeAiTraces && (
                                    <button
                                        onClick={() => setActiveComposerTab('ai-log')}
                                        className={`flex-1 py-3 text-sm font-medium transition-all ${activeComposerTab === 'ai-log' ? 'text-violet-600 border-b-2 border-violet-600 bg-violet-50 dark:bg-violet-900/20' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                    >
                                        <span className="flex items-center justify-center gap-2">
                                            <span className={`material-symbols-outlined text-lg ${activeComposerTab === 'ai-log' ? 'text-violet-600' : ''}`}>psychology_alt</span>
                                            Log IA
                                        </span>
                                    </button>
                                )}
                            </div>
                            
                            {activeComposerTab === 'note' ? (
                                <div
                                    className={`p-4 transition-colors ${isDragging ? 'bg-primary/5 border-2 border-dashed border-primary rounded-b-xl' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <textarea
                                        ref={textareaRef}
                                        className="w-full border-0 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 focus:ring-0 text-sm resize-none"
                                        placeholder={`Escribe una nota sobre ${contact.first_name || 'el contacto'}...`}
                                        rows="3"
                                        value={noteBody}
                                        onChange={(e) => setNoteBody(e.target.value)}
                                        disabled={createNoteMutation.isPending}
                                    ></textarea>

                                    {attachments.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                                            {(attachments || []).map((file, idx) => (
                                                <div key={idx} className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs text-gray-700 dark:text-gray-300">
                                                    <span className="material-symbols-outlined text-[14px]">attach_file</span>
                                                    <span className="truncate max-w-[120px]">{file.name}</span>
                                                    <button onClick={() => removeAttachment(idx)} className="hover:text-red-500 ml-1">
                                                        <span className="material-symbols-outlined text-[14px]">close</span>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                                        <div className="flex items-center gap-2 text-gray-400 min-w-0">
                                            <input type="file" multiple className="hidden" ref={fileInputRef} onChange={handleFileChange} />
                                            <button onClick={() => fileInputRef.current?.click()} className="hover:text-primary dark:hover:text-primary p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Adjuntar archivo"><span className="material-symbols-outlined text-xl">attach_file</span></button>
                                            <button onClick={() => insertFormat('**', '**')} className="hover:text-gray-900 dark:hover:text-white p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Negrita"><span className="material-symbols-outlined text-xl">format_bold</span></button>
                                            <button onClick={() => insertFormat('- ')} className="hover:text-gray-900 dark:hover:text-white p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Lista"><span className="material-symbols-outlined text-xl">format_list_bulleted</span></button>
                                            <DictateButton
                                                onAppend={setNoteBody}
                                                disabled={createNoteMutation.isPending}
                                                size={20}
                                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                                            />
                                        </div>
                                        <button
                                            className="bg-primary hover:bg-primary/90 disabled:opacity-70 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm flex items-center gap-2"
                                            onClick={handleSaveNote}
                                            disabled={createNoteMutation.isPending || (!noteBody.trim() && attachments.length === 0)}
                                        >
                                            {createNoteMutation.isPending ? <span className="material-symbols-outlined text-sm animate-spin">sync</span> : null}
                                            Guardar Nota
                                        </button>
                                    </div>
                                </div>
                            ) : activeComposerTab === 'followup' ? (
                                <div className="p-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Tipo</label>
                                            <select
                                                value={followUpForm.type}
                                                onChange={(e) => setFollowUpForm(prev => ({ ...prev, type: e.target.value }))}
                                                className="w-full h-10 rounded-lg border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm focus:border-primary focus:ring-primary/20"
                                            >
                                                {FOLLOW_UP_TYPES.map(t => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha y hora</label>
                                            <input
                                                type="datetime-local"
                                                value={followUpForm.scheduled_at}
                                                onChange={(e) => setFollowUpForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                                                className="w-full h-10 rounded-lg border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm focus:border-primary focus:ring-primary/20"
                                            />
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={`Ej: Llamar a ${contact.first_name || 'el contacto'} para dar seguimiento`}
                                        value={followUpForm.title}
                                        onChange={(e) => setFollowUpForm(prev => ({ ...prev, title: e.target.value }))}
                                        className="w-full mt-3 h-10 rounded-lg border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm focus:border-primary focus:ring-primary/20"
                                    />
                                    <textarea
                                        placeholder="Nota adicional (opcional)"
                                        rows="2"
                                        value={followUpForm.description}
                                        onChange={(e) => setFollowUpForm(prev => ({ ...prev, description: e.target.value }))}
                                        className="w-full mt-2 rounded-lg border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white text-sm resize-none focus:border-primary focus:ring-primary/20"
                                    />
                                    <div className="flex justify-end mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                                        <button
                                            className="bg-primary hover:bg-primary/90 disabled:opacity-70 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors shadow-sm flex items-center gap-2"
                                            onClick={handleScheduleFollowUp}
                                            disabled={createActivityMutation.isPending || !followUpForm.title.trim()}
                                        >
                                            {createActivityMutation.isPending ? <span className="material-symbols-outlined text-sm animate-spin">sync</span> : <span className="material-symbols-outlined text-sm">event_available</span>}
                                            Agendar Seguimiento
                                        </button>
                                    </div>
                                </div>
                            ) : activeComposerTab === 'ai-log' ? (
                                <div className="p-0 max-h-[400px] overflow-y-auto no-scrollbar bg-gray-50/30 dark:bg-gray-900/10">
                                    {isLoadingTraces ? (
                                        <div className="p-10 flex flex-col items-center justify-center gap-3">
                                            <div className="w-6 h-6 border-2 border-violet-500/30 border-t-violet-600 rounded-full animate-spin"></div>
                                            <p className="text-xs text-gray-400">Cargando trazas de pensamiento...</p>
                                        </div>
                                    ) : aiTraces.length === 0 ? (
                                        <div className="p-10 flex flex-col items-center justify-center gap-2 text-center">
                                            <span className="material-symbols-outlined text-4xl text-gray-200 dark:text-gray-800">psychology_alt</span>
                                            <p className="text-sm font-medium text-gray-400">No hay logs de IA para este contacto.</p>
                                            <p className="text-[11px] text-gray-400 max-w-[200px]">Las trazas se generan cuando la IA evalúa al lead o responde automáticamente.</p>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {aiTraces.map((trace) => (
                                                <div key={trace.id} className="p-4 hover:bg-white dark:hover:bg-gray-800/20 transition-colors">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                                                trace.action_type === 'evaluation' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
                                                                trace.action_type === 'autopilot_response' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' :
                                                                'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                            }`}>
                                                                {trace.action_type === 'evaluation' ? 'Evaluación' : trace.action_type === 'autopilot_response' ? 'Respuesta' : trace.action_type}
                                                            </span>
                                                            <span className="text-[11px] text-gray-400">{dayjs(trace.created_at).fromNow()}</span>
                                                        </div>
                                                        <span className="text-[10px] text-gray-400">{dayjs(trace.created_at).format('HH:mm:ss')}</span>
                                                    </div>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-medium">
                                                        {trace.thought || 'Sin razonamiento explícito.'}
                                                    </p>
                                                    {trace.data?.score !== undefined && (
                                                        <div className="mt-2 flex items-center gap-2">
                                                            <span className="text-[10px] font-bold text-gray-400 uppercase">Lead Score:</span>
                                                            <span className={`text-xs font-bold ${trace.data.score >= 70 ? 'text-green-500' : trace.data.score >= 40 ? 'text-yellow-500' : 'text-gray-500'}`}>
                                                                {trace.data.score}%
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="p-10 text-center text-gray-400 text-sm">
                                    Módulo en desarrollo.
                                </div>
                            )}
                        </div>
                        )}

                        {/* Timeline Filter */}
                        <div className="flex items-center justify-between px-2">
                            <h3 className="font-bold text-gray-900 dark:text-white">Actividad</h3>
                            <div className="flex gap-1 text-xs flex-wrap justify-end">
                                {[
                                    { key: 'todo',     label: 'Todo' },
                                    { key: 'notas',    label: 'Notas' },
                                    { key: 'chats',    label: 'Chats' },
                                    ...(hasEmailAccounts ? [{ key: 'emails', label: 'Emails' }] : []),
                                    { key: 'archivos', label: 'Archivos' },
                                    { key: 'llamadas', label: 'Llamadas' },
                                ].map(f => (
                                    <button
                                        key={f.key}
                                        onClick={() => setTimelineFilter(f.key)}
                                        className={`px-3 py-1.5 rounded-full font-medium transition-colors ${timelineFilter === f.key ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Timeline Stream */}
                        <div className="relative space-y-8 pl-4 before:absolute before:inset-0 before:ml-4 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent dark:before:via-gray-800 before:pointer-events-none">

                            {combinedTimeline.length === 0 && (
                                <div className="text-center py-10 text-gray-500 italic">No hay actividad para mostrar.</div>
                            )}

                            {(combinedTimeline || []).map(item => {

                                // ── NOTE ──
                                if (item.type === 'note') {
                                    return (
                                        <div key={item.id} className="relative flex items-start gap-4 group">
                                            <div className="absolute left-0 mt-1 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-yellow-100 ring-4 ring-background-light dark:ring-background-dark dark:bg-yellow-900/50">
                                                <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400 text-base">sticky_note_2</span>
                                            </div>
                                            <div className="flex-1 ml-6 bg-yellow-50/50 dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-800/30 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-semibold text-gray-900 dark:text-white text-sm">{item.title}</span>
                                                        <span className="text-xs text-gray-500">por {item.user}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-400" title={dayjs(item.created_at).format('DD MMM YYYY HH:mm')}>{dayjs(item.created_at).fromNow()}</span>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => handleDeleteClick(item.noteId, e)}
                                                            disabled={deleteNoteMutation.isPending}
                                                            className="relative z-20 text-gray-400 hover:text-red-500 transition-colors p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center disabled:opacity-50 cursor-pointer"
                                                            title="Eliminar nota"
                                                        >
                                                            <span className="material-symbols-outlined text-[16px] pointer-events-none">delete</span>
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                                                    <ReactMarkdown
                                                        components={{
                                                            p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                                                            ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2" {...props} />,
                                                            ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                                                            strong: ({node, ...props}) => <strong className="font-semibold text-gray-900 dark:text-white" {...props} />,
                                                            a: ({node, ...props}) => <a className="text-primary hover:underline" {...props} />,
                                                        }}
                                                    >
                                                        {item.body}
                                                    </ReactMarkdown>
                                                </div>
                                                {item.attachments?.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-yellow-100 dark:border-yellow-800/30 flex flex-wrap gap-2">
                                                        {(item.attachments || []).map((attachment, idx) => {
                                                            let url = typeof attachment === 'string' ? attachment : (attachment.path || '');
                                                            let filename = typeof attachment === 'string' ? (url.split('/').pop() || 'Adjunto') : (attachment.name || url.split('/').pop());
                                                            return (
                                                                <a
                                                                    key={idx}
                                                                    href={url}
                                                                    onClick={(e) => handleDownload(url, filename, e)}
                                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-yellow-200 dark:border-yellow-700/50 bg-yellow-100 dark:bg-yellow-800/20 text-xs text-yellow-800 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-700/50 transition-colors cursor-pointer"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">download</span>
                                                                    <span className="truncate max-w-[150px]">{filename}</span>
                                                                </a>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }

                                // ── EMAIL (from activity) ──
                                if (item.type === 'email') {
                                    return (
                                        <div key={item.id} className="relative flex items-start gap-4 group">
                                            <div className="absolute left-0 mt-1 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-blue-100 ring-4 ring-background-light dark:ring-background-dark dark:bg-blue-900/50">
                                                <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-base">mail</span>
                                            </div>
                                            <div
                                                className="flex-1 ml-6 bg-white dark:bg-[#1a100c] border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all cursor-pointer"
                                                onClick={() => setSelectedEmailId(item.emailId || item.metadata?.email_message_id)}
                                            >
                                                <div className="px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                                                    <div className="flex items-center gap-2 overflow-hidden mr-4">
                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase flex-shrink-0 ${item.metadata?.is_inbound ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'}`}>
                                                            {item.metadata?.is_inbound ? 'Recibido' : 'Enviado'}
                                                        </span>
                                                        <span className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.title}</span>
                                                    </div>
                                                    <span className="text-xs text-gray-400 whitespace-nowrap" title={dayjs(item.created_at).format('DD MMM YYYY HH:mm')}>{dayjs(item.created_at).fromNow()}</span>
                                                </div>
                                                <div className="p-4">
                                                    <p className="text-xs text-gray-400 mb-1">
                                                        {item.metadata?.is_inbound ? `De: ${item.metadata?.from_email}` : `Para: ${item.metadata?.to_email}`}
                                                    </p>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                                        {item.body || <em>Sin vista previa</em>}
                                                    </p>
                                                    {item.metadata?.has_attachments && (
                                                        <div className="mt-3 flex gap-2">
                                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs text-gray-500">
                                                                <span className="material-symbols-outlined text-sm text-gray-500">attachment</span> Contiene adjuntos
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // ── ACTIVITY (call, meeting, task, whatsapp, system, form) ──
                                if (['call', 'meeting', 'task', 'whatsapp', 'system', 'form'].includes(item.type)) {
                                    const meta = getFollowUpTypeMeta(item.type);
                                    const isScheduled = !!item.scheduled_at;
                                    const overdue = isScheduled && item.is_overdue && !item.is_completed;
                                    return (
                                        <div key={item.id} className="relative flex items-start gap-4 group">
                                            <div className={`absolute left-0 mt-1 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full ring-4 ring-background-light dark:ring-background-dark ${item.is_completed ? 'bg-emerald-100 dark:bg-emerald-900/50' : overdue ? 'bg-red-100 dark:bg-red-900/50' : meta.bg}`}>
                                                <span className={`material-symbols-outlined text-base ${item.is_completed ? 'text-emerald-600 dark:text-emerald-400' : overdue ? 'text-red-600 dark:text-red-400' : meta.color}`}>
                                                    {item.is_completed ? 'check_circle' : meta.icon}
                                                </span>
                                            </div>
                                            <div className={`flex-1 ml-6 bg-white dark:bg-[#1a100c] border rounded-xl p-4 shadow-sm flex items-center justify-between gap-3 ${overdue ? 'border-red-200 dark:border-red-900/40' : 'border-gray-100 dark:border-gray-800'}`}>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <span className={`font-semibold text-sm ${item.is_completed ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>{item.title}</span>
                                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.bg} ${meta.color}`}>{meta.label}</span>
                                                        {overdue && (
                                                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400">Vencido</span>
                                                        )}
                                                    </div>
                                                    {item.body && <p className="text-sm text-gray-500">{item.body}</p>}
                                                    {isScheduled && (
                                                        <p className={`text-xs mt-1 flex items-center gap-1 ${overdue ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                                                            <span className="material-symbols-outlined text-[13px]">schedule</span>
                                                            {dayjs(item.scheduled_at).format('DD MMM YYYY, HH:mm')}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-2 shrink-0">
                                                    <span className="text-xs text-gray-400 whitespace-nowrap" title={dayjs(item.created_at).format('DD MMM YYYY HH:mm')}>{dayjs(item.created_at).fromNow()}</span>
                                                    {!item.is_completed && item.activityId && (
                                                        <button
                                                            onClick={() => completeActivityMutation.mutate(item.activityId)}
                                                            disabled={completeActivityMutation.isPending}
                                                            className="text-[11px] font-semibold text-primary hover:underline disabled:opacity-50 flex items-center gap-1"
                                                        >
                                                            <span className="material-symbols-outlined text-[13px]">check</span>
                                                            Completar
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // ── CONVERSATION (WhatsApp / Instagram) ──
                                if (item.type === 'conversation') {
                                    const isInstagram = item.channel === 'instagram';
                                    return (
                                        <div key={item.id} className="relative flex items-start gap-4 group">
                                            <div className={`absolute left-0 mt-1 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full ring-4 ring-background-light dark:ring-background-dark ${isInstagram ? 'bg-pink-100 dark:bg-pink-900/50' : 'bg-green-100 dark:bg-green-900/50'}`}>
                                                <span className={`material-symbols-outlined text-base ${isInstagram ? 'text-pink-600 dark:text-pink-400' : 'text-green-600 dark:text-green-400'}`}>
                                                    {isInstagram ? 'photo_camera' : 'chat'}
                                                </span>
                                            </div>
                                            <div
                                                className={`flex-1 ml-6 bg-white dark:bg-[#1a100c] border rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer ${isInstagram ? 'border-pink-100 dark:border-pink-900/30 hover:border-pink-200 dark:hover:border-pink-700' : 'border-green-100 dark:border-green-900/30 hover:border-green-200 dark:hover:border-green-700'}`}
                                                onClick={() => navigate(`/chat?conversation=${item.conversationId}`)}
                                            >
                                                <div className={`px-4 py-3 border-b flex justify-between items-center ${isInstagram ? 'bg-pink-50/30 dark:bg-pink-900/10 border-pink-100 dark:border-pink-900/20' : 'bg-green-50/30 dark:bg-green-900/10 border-green-100 dark:border-green-900/20'}`}>
                                                    <div className="flex items-center gap-2 overflow-hidden mr-4 flex-wrap">
                                                        <ChannelBadge channel={item.channel} />
                                                        <span className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.title}</span>
                                                        {item.unread_count > 0 && (
                                                            <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold">
                                                                {item.unread_count}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-gray-400 whitespace-nowrap" title={dayjs(item.created_at).format('DD MMM YYYY HH:mm')}>{dayjs(item.created_at).fromNow()}</span>
                                                </div>
                                                <div className="p-4">
                                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 italic">
                                                        {item.body || <span className="not-italic text-gray-400">Sin mensajes aún</span>}
                                                    </p>
                                                    <div className="mt-3 flex gap-2">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium transition-colors ${isInstagram ? 'border-pink-200 dark:border-pink-700 text-pink-600 dark:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/20' : 'border-green-200 dark:border-green-700 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'}`}>
                                                            <span className="material-symbols-outlined text-sm">open_in_new</span>
                                                            Abrir chat
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // ── MEDIA ──
                                if (item.type === 'media') {
                                    const fi = getFileInfo(item.mediaMime, item.mediaType, item.body);
                                    return (
                                        <div key={item.id} className="relative flex items-start gap-4 group">
                                            <div className="absolute left-0 mt-1 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-teal-100 ring-4 ring-background-light dark:ring-background-dark dark:bg-teal-900/50">
                                                <span className={`material-symbols-outlined text-base ${fi.color}`}>{fi.icon}</span>
                                            </div>
                                            <div className="flex-1 ml-6 bg-white dark:bg-[#1a100c] border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all">
                                                <div className="px-4 py-3 bg-gray-50/50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <ChannelBadge channel={item.channel || 'whatsapp'} />
                                                        <span className="font-semibold text-gray-900 dark:text-white text-sm">{item.title}</span>
                                                    </div>
                                                    <span className="text-xs text-gray-400 whitespace-nowrap" title={dayjs(item.created_at).format('DD MMM YYYY HH:mm')}>{dayjs(item.created_at).fromNow()}</span>
                                                </div>
                                                <div className="p-4">
                                                    {item.mediaType === 'image' && (
                                                        <img src={resolveMediaUrl(item.mediaUrl)} className="max-w-xs max-h-64 object-cover rounded shadow-sm mb-2 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => window.open(resolveMediaUrl(item.mediaUrl), '_blank')} alt="Adjunto" />
                                                    )}
                                                    {item.mediaType === 'sticker' && (
                                                        <img src={resolveMediaUrl(item.mediaUrl)} className="w-32 h-32 object-contain drop-shadow-sm mb-2" alt="Sticker" />
                                                    )}
                                                    {(item.mediaType === 'audio' || item.mediaType === 'voice_note') && (
                                                        <div className="mb-2 w-full min-w-[280px] sm:min-w-[320px] py-1">
                                                            <audio 
                                                                src={resolveMediaUrl(item.mediaUrl)} 
                                                                controls 
                                                                className="w-full h-10 filter dark:invert dark:hue-rotate-180 opacity-90 hover:opacity-100 transition-opacity" 
                                                            />
                                                        </div>
                                                    )}
                                                    {item.mediaType === 'video' && (
                                                        item.mediaUrl ? (
                                                            <div className="relative mb-2">
                                                                <video src={resolveMediaUrl(item.mediaUrl)} controls className="max-w-xs max-h-64 rounded shadow-sm" />
                                                                <a
                                                                    href={resolveMediaUrl(item.mediaUrl)}
                                                                    download
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
                                                                    title="Descargar video"
                                                                    onClick={e => e.stopPropagation()}
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px] leading-none block">download</span>
                                                                </a>
                                                            </div>
                                                        ) : (
                                                            /* Video not yet downloaded — show preview card with link to chat */
                                                            <Link
                                                                to={`/chat?conversation=${item.conversationId}`}
                                                                className="block relative w-56 h-32 bg-gray-900 dark:bg-black/60 rounded overflow-hidden mb-2 group/vidcard360"
                                                                title="Abre el chat para reproducir este video"
                                                            >
                                                                <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 dark:from-gray-800 dark:to-black" />
                                                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                                                                    <div className="w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors group-hover/vidcard360:scale-110 transition-transform">
                                                                        <span className="material-symbols-outlined text-white text-[28px] leading-none ml-0.5">play_arrow</span>
                                                                    </div>
                                                                    <span className="text-white text-[10px] bg-black/50 rounded-full px-2 py-0.5">Abrir en chat</span>
                                                                </div>
                                                                <div className="absolute top-1.5 left-1.5 bg-black/50 rounded p-0.5">
                                                                    <span className="material-symbols-outlined text-white text-[14px] leading-none block">videocam</span>
                                                                </div>
                                                            </Link>
                                                        )
                                                    )}
                                                    {item.mediaType === 'document' && (
                                                        <a href={resolveMediaUrl(item.mediaUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline mb-2 bg-primary/5 px-3 py-2 rounded">
                                                            <span className="material-symbols-outlined">download</span> Descargar Documento
                                                        </a>
                                                    )}

                                                    {/* ── STATUS REPLY CARD (360 Timeline) ── */}
                                                    {item.metadata?.is_status_reply && (
                                                        <div className="mb-2 rounded-xl overflow-hidden border-l-[3px] border-purple-500 bg-purple-50 dark:bg-purple-900/20">
                                                            {item.metadata?.quoted_status_thumbnail_url ? (
                                                                <div className="relative">
                                                                    <img
                                                                        src={resolveMediaUrl(item.metadata.quoted_status_thumbnail_url)}
                                                                        alt="Estado citado"
                                                                        className="w-full max-h-32 object-cover opacity-80"
                                                                    />
                                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2">
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="material-symbols-outlined text-white text-[13px]">motion_photos_on</span>
                                                                            <span className="text-white text-[11px] font-bold uppercase">Estado de WhatsApp</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-2 px-3 py-2">
                                                                    <span className="material-symbols-outlined text-purple-500 text-lg">motion_photos_on</span>
                                                                    <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">Comentó un Estado</span>
                                                                </div>
                                                            )}
                                                            {item.metadata?.quoted_status_content && (
                                                                <p className="px-3 py-1.5 text-xs text-purple-700 dark:text-purple-300 italic">{item.metadata.quoted_status_content}</p>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* ── LOCATION CARD (360 Timeline) ── */}
                                                    {item.mediaType === 'location' && item.metadata?.latitude && (
                                                        <a
                                                            href={`https://www.google.com/maps?q=${item.metadata.latitude},${item.metadata.longitude}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="block mb-2 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-primary/50 transition-colors group/loccard360"
                                                        >
                                                            <div className="relative h-24 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                                                <div className="w-10 h-10 rounded-full bg-red-500 shadow-lg flex items-center justify-center ring-4 ring-white/60">
                                                                    <span className="material-symbols-outlined text-white text-[22px]">location_on</span>
                                                                </div>
                                                            </div>
                                                            <div className="bg-white dark:bg-gray-900/50 px-3 py-2">
                                                                {item.metadata.place_name && <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{item.metadata.place_name}</p>}
                                                                {item.metadata.address && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.metadata.address}</p>}
                                                                <p className="text-xs text-primary font-medium mt-1 flex items-center gap-0.5">
                                                                    <span className="material-symbols-outlined text-[11px]">near_me</span> Abrir en Maps
                                                                </p>
                                                            </div>
                                                        </a>
                                                    )}

                                                    {/* ── CONTACT CARD (360 Timeline) ── */}
                                                    {item.mediaType === 'contact' && (
                                                        <div className="mb-2 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                                            <div className="flex items-center gap-3 px-4 py-3">
                                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                                                                    <span className="material-symbols-outlined text-white text-[22px]">person</span>
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">{item.metadata?.contact_name || 'Contacto'}</p>
                                                                    {item.metadata?.contact_phone && <p className="text-xs text-gray-500">+{item.metadata.contact_phone}</p>}
                                                                </div>
                                                            </div>
                                                            {item.metadata?.contact_phone && (
                                                                <a
                                                                    href={`https://wa.me/${item.metadata.contact_phone}`}
                                                                    target="_blank" rel="noreferrer"
                                                                    className="flex items-center justify-center gap-1.5 py-1.5 border-t border-gray-100 dark:border-gray-800 text-xs text-green-600 dark:text-green-400 font-medium hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                                                >
                                                                    <span className="material-symbols-outlined text-[14px]">whatsapp</span> Enviar WhatsApp
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* ── BUSINESS CARD (360 Timeline) ── */}
                                                    {item.mediaType === 'business' && (() => {
                                                        const btype = item.metadata?.business_type;
                                                        if (btype === 'product') return (
                                                            <div className="mb-2 rounded-xl border border-amber-200 dark:border-amber-800/40 overflow-hidden bg-amber-50 dark:bg-amber-900/10">
                                                                {item.metadata.product_url && <img src={item.metadata.product_url} alt={item.metadata.product_title} className="w-full h-24 object-cover" onError={e => e.target.style.display='none'} />}
                                                                <div className="px-3 py-2">
                                                                    <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-0.5">Catálogo WhatsApp Business</p>
                                                                    <p className="font-semibold text-sm text-gray-800 dark:text-gray-100">{item.metadata.product_title}</p>
                                                                    {item.metadata.product_price && <p className="text-sm font-bold text-primary mt-0.5">{item.metadata.product_currency} {(item.metadata.product_price / 1000).toFixed(2)}</p>}
                                                                </div>
                                                            </div>
                                                        );
                                                        if (btype === 'order') return (
                                                            <div className="mb-2 rounded-xl border border-orange-200 dark:border-orange-800/40 bg-orange-50 dark:bg-orange-900/10 px-3 py-2.5">
                                                                <div className="flex items-center gap-1.5 mb-1">
                                                                    <span className="material-symbols-outlined text-orange-500 text-lg">shopping_cart</span>
                                                                    <span className="text-xs font-bold text-orange-700 dark:text-orange-400">Pedido #{item.metadata.order_id}</span>
                                                                </div>
                                                                {item.metadata.item_count > 0 && <p className="text-xs text-gray-600 dark:text-gray-400">{item.metadata.item_count} artículo(s)</p>}
                                                            </div>
                                                        );
                                                        if (btype === 'list_response' || btype === 'buttons_response' || btype === 'template_reply') return (
                                                            <div className="mb-2 rounded-xl border border-blue-200 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-900/10 px-3 py-2 flex items-center gap-2">
                                                                <span className="material-symbols-outlined text-blue-500">{btype === 'list_response' ? 'checklist' : 'touch_app'}</span>
                                                                <div>
                                                                    <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase">{btype === 'list_response' ? 'Opción de lista seleccionada' : 'Botón seleccionado'}</p>
                                                                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{item.metadata.selected_title || item.metadata.selected_id}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                        return null;
                                                    })()}

                                                    {item.body && <p className="text-sm text-gray-600 dark:text-gray-400 italic break-words">{item.body}</p>}
                                                    <div className="mt-3">
                                                        <Link to={`/chat?conversation=${item.conversationId}`} className="text-xs text-primary hover:underline">Ver en el chat</Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                return null;
                            })}
                        </div>
                    </section>

                    {/* ── RIGHT COLUMN: Deals + Files Panel (3 cols) ── */}
                    <section className="lg:col-span-3 flex flex-col gap-6">
                        {/* Active Deals */}
                        {!canUseDeals ? null : contact.deals?.length > 0 ? (
                            <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Negocios</h3>
                                    <button onClick={() => { setDealFormData(f => ({ ...f, currency: dealDefaultCurrency })); setIsAddDealModalOpen(true); }} className="text-primary hover:text-primary/80"><span className="material-symbols-outlined text-lg">add_circle</span></button>
                                </div>
                                <div className="p-4 flex flex-col gap-3">
                                    {(contact.deals || []).map(deal => (
                                        <div key={deal.id} className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-primary/50 transition-colors cursor-pointer group">
                                            <div className="flex justify-between items-start mb-2">
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary transition-colors">{deal.title}</h4>
                                                <span className="text-xs font-bold text-gray-900 dark:text-white">${parseFloat(deal.value || 0).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between text-xs text-gray-500 mt-2">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800">{deal.stage}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/30 text-center border-t border-gray-100 dark:border-gray-800">
                                    <Link to="/deals" className="text-xs font-medium text-primary hover:underline">Ver todos los negocios</Link>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-[#1a100c] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-5 text-center">
                                <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-700 mb-2">monetization_on</span>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Sin negocios</h3>
                                <p className="text-xs text-gray-500 mt-1 mb-3">No hay negocios asociados a este contacto.</p>
                                <button onClick={() => { setDealFormData(f => ({ ...f, currency: dealDefaultCurrency })); setIsAddDealModalOpen(true); }} className="text-xs font-medium text-primary hover:underline flex items-center justify-center gap-1 mx-auto"><span className="material-symbols-outlined text-sm">add_circle</span> Crear Negocio</button>
                            </div>
                        )}

                        {/* ── Cotizaciones ── */}
                        <ContactQuotesCard contactId={contact.id} />

                        {/* ── Files Panel ── */}
                        <FilesPanel files={allFiles} />
                    </section>

                </div>
            </div>

            {/* Edit Contact Modal */}
            <ContactModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                contact={contact}
            />

            {/* Delete Note Confirmation */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsDeleteModalOpen(false)}></div>
                    <div className="relative bg-white dark:bg-[#1a100c] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 text-center">
                            <div className="size-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-3xl">delete_forever</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">¿Eliminar actividad?</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                Esta acción eliminará permanentemente la nota y todos sus archivos adjuntos. No se puede deshacer.
                            </p>
                        </div>
                        <div className="flex border-t border-gray-100 dark:border-gray-800">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="flex-1 px-4 py-4 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={deleteNoteMutation.isPending}
                                className="flex-1 px-4 py-4 text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                            >
                                {deleteNoteMutation.isPending && <span className="material-symbols-outlined animate-spin text-sm">sync</span>}
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Contact Confirmation */}
            {isDeleteContactModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsDeleteContactModalOpen(false)}></div>
                    <div className="relative bg-white dark:bg-[#1a100c] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 text-center">
                            <div className="size-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto mb-4">
                                <span className="material-symbols-outlined text-3xl">delete_forever</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">¿Eliminar lead?</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                Esta acción eliminará permanentemente al lead <strong>{contact.full_name || 'este contacto'}</strong> y todo su historial de conversaciones, notas y actividades. No se puede deshacer.
                            </p>
                        </div>
                        <div className="flex border-t border-gray-100 dark:border-gray-800">
                            <button
                                onClick={() => setIsDeleteContactModalOpen(false)}
                                className="flex-1 px-4 py-4 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => deleteContactMutation.mutate()}
                                disabled={deleteContactMutation.isPending}
                                className="flex-1 px-4 py-4 text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                            >
                                {deleteContactMutation.isPending && <span className="material-symbols-outlined animate-spin text-sm">sync</span>}
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Viewer Modal */}
            <EmailViewerModal
                emailId={selectedEmailId}
                onClose={() => setSelectedEmailId(null)}
            />

            {/* Merge Contact Modal */}
            <MergeContactModal
                master={contact}
                isOpen={isMergeModalOpen}
                onClose={() => setIsMergeModalOpen(false)}
                onSuccess={(updatedContact) => {
                    queryClient.invalidateQueries({ queryKey: ['contact', id] });
                }}
            />

            {/* Add Deal Modal */}
            {isAddDealModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={() => setIsAddDealModalOpen(false)}></div>
                    <div className="relative bg-white dark:bg-[#1a100c] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
                            <h3 className="font-bold text-gray-900 dark:text-white text-base">Nuevo Negocio para {contact.first_name}</h3>
                            <button onClick={() => setIsAddDealModalOpen(false)} className="text-gray-400 hover:text-primary"><span className="material-symbols-outlined text-lg">close</span></button>
                        </div>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            createDealMutation.mutate({
                                ...dealFormData,
                                value: dealFormData.value ? parseFloat(dealFormData.value) : 0
                            });
                        }} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs uppercase font-bold text-gray-400 mb-2">Título del Negocio *</label>
                                <input
                                    type="text"
                                    required
                                    value={dealFormData.title}
                                    onChange={(e) => setDealFormData({ ...dealFormData, title: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg focus:ring-1 focus:ring-primary outline-none text-gray-900 dark:text-white text-sm"
                                    placeholder="Ej: Compra de granito"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase font-bold text-gray-400 mb-2">Valor</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={dealFormData.value}
                                        onChange={(e) => setDealFormData({ ...dealFormData, value: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg focus:ring-1 focus:ring-primary outline-none text-gray-900 dark:text-white text-sm"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase font-bold text-gray-400 mb-2">Moneda</label>
                                    <select
                                        value={dealFormData.currency}
                                        onChange={(e) => setDealFormData({ ...dealFormData, currency: e.target.value })}
                                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg focus:ring-1 focus:ring-primary outline-none text-gray-900 dark:text-white text-sm"
                                    >
                                        {CURRENCIES.map((c) => (
                                            <option key={c.code} value={c.code}>{currencyOptionLabel(c)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase font-bold text-gray-400 mb-2">Etapa</label>
                                <select
                                    value={dealFormData.stage}
                                    onChange={(e) => setDealFormData({ ...dealFormData, stage: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg focus:ring-1 focus:ring-primary outline-none text-gray-900 dark:text-white text-sm"
                                >
                                    <option value="prospecting">Prospección</option>
                                    <option value="qualification">Calificación</option>
                                    <option value="proposal">Propuesta</option>
                                    <option value="negotiation">Negociación</option>
                                    <option value="closed_won">Ganado</option>
                                    <option value="closed_lost">Perdido</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs uppercase font-bold text-gray-400 mb-2">Fecha Prevista de Cierre</label>
                                <input
                                    type="date"
                                    value={dealFormData.expected_close_date}
                                    onChange={(e) => setDealFormData({ ...dealFormData, expected_close_date: e.target.value })}
                                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg focus:ring-1 focus:ring-primary outline-none text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setIsAddDealModalOpen(false)}
                                    className="px-4 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={createDealMutation.isPending}
                                    className="px-4 py-2 text-xs font-bold text-white bg-primary hover:bg-primary/95 rounded-lg shadow-sm flex items-center gap-1.5"
                                >
                                    {createDealMutation.isPending && <span className="material-symbols-outlined animate-spin text-[14px]">sync</span>}
                                    Crear Negocio
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
